import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SpeedIcon from '@mui/icons-material/Speed';
import ImportSummary from '../../../components/csv-import/ImportSummary';
import ErrorList from '../../../components/csv-import/ErrorList';
import { ImportResult } from '../../../types/fuelImport';

/**
 * Page de résultats d'importation pour l'importation de carburant
 */
const FuelResultsPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Étapes de l'importation
  const steps = [
    'Sélection du fichier',
    'Configuration du mapping',
    'Importation des données',
    'Résultats'
  ];
  
  // Charger les données du localStorage au chargement de la page
  useEffect(() => {
    const storedResult = localStorage.getItem('fuelImport_result');
    
    if (!storedResult) {
      // Rediriger vers la page d'importation si les données ne sont pas disponibles
      navigate('/admin/fuel/import');
      return;
    }
    
    try {
      setResult(JSON.parse(storedResult));
    } catch (error) {
      console.error('Erreur lors du chargement des résultats:', error);
      setError('Impossible de charger les résultats. Veuillez réessayer.');
      navigate('/admin/fuel/import');
    }
  }, [navigate]);
  
  // Gérer le clic sur le bouton "Nouvelle importation"
  const handleNewImport = () => {
    // Nettoyer le localStorage
    localStorage.removeItem('fuelImport_headers');
    localStorage.removeItem('fuelImport_data');
    localStorage.removeItem('fuelImport_fileName');
    localStorage.removeItem('fuelImport_fileSize');
    localStorage.removeItem('fuelImport_supplierId');
    localStorage.removeItem('fuelImport_mapping');
    localStorage.removeItem('fuelImport_dateFormat');
    localStorage.removeItem('fuelImport_saveConfig');
    localStorage.removeItem('fuelImport_newSupplierName');
    localStorage.removeItem('fuelImport_result');
    
    // Rediriger vers la page d'importation
    navigate('/admin/fuel/import');
  };
  
  // Gérer le clic sur le bouton "Gérer les véhicules non reconnus"
  const handleManageUnknownVehicles = () => {
    navigate('/admin/fuel/import/unknown-vehicles');
  };
  
  // Gérer le clic sur le bouton "Voir l'historique des importations"
  const handleViewHistory = () => {
    navigate('/admin/fuel/import/history');
  };
  
  // Formater le résumé pour le composant ImportSummary
  const formatSummary = (result: ImportResult) => {
    return {
      totalTransactions: result.totalTransactions,
      processedTransactions: result.processedTransactions,
      skippedTransactions: result.skippedTransactions,
      fuelConsumptionsCreated: result.fuelConsumptionsCreated,
      mileageUpdates: result.mileageUpdates,
      unknownVehiclesCount: result.unknownVehicles.length,
      errorsCount: result.errors.length,
      processingTime: 0 // Non disponible pour le moment
    };
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Résultats de l'importation
        </Typography>
        
        <Stepper activeStep={3} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {result && (
          <>
            <ImportSummary summary={formatSummary(result)} />
            
            {result.unknownVehicles.length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Véhicules non reconnus
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {result.unknownVehicles.length} véhicule(s) n'ont pas été reconnus dans le système.
                  Vous pouvez les créer pour traiter les transactions associées.
                </Typography>
                
                <List>
                  {result.unknownVehicles.slice(0, 5).map((vehicle, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <DirectionsCarIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary={vehicle.matricule}
                        secondary={`${vehicle.count} transaction${vehicle.count > 1 ? 's' : ''}`}
                      />
                      <Chip
                        label="Non reconnu"
                        color="warning"
                        variant="outlined"
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
                
                {result.unknownVehicles.length > 5 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Et {result.unknownVehicles.length - 5} autre(s) véhicule(s)...
                  </Typography>
                )}
                
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleManageUnknownVehicles}
                  >
                    Gérer les véhicules non reconnus
                  </Button>
                </Box>
              </Paper>
            )}
            
            {result.errors.length > 0 && (
              <ErrorList
                errors={result.errors}
                title="Erreurs d'importation"
                initialLimit={5}
              />
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleViewHistory}
              >
                Voir l'historique des importations
              </Button>
              
              <Button
                variant="contained"
                onClick={handleNewImport}
              >
                Nouvelle importation
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default FuelResultsPage;